import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { AddListingCalculator } from "./AddListingCalculator";
import { ClientOnly } from "~/components/blocks/ClientOnly/client-only";
import { useNavigate } from "@remix-run/react";
import { PAGINATION_ITEM_PER_PAGE } from "~/lib/constants/generalConstants";
import { getAverageCostPerWorkstation } from "~/helpers/ArrayHelpers";
import { parseSearchUrl } from "~/lib/helpers/StringHelper";
import PropertiesService from "~/lib/services/PropertiesService";
import CustomMap from "~/components/blocks/Map/map.client";
interface SearchFilters {
	desks: number
	office: "premium" | "superior" | "economical"
	spaceType: string
	area: number
	units: string
	location: string
	state: string
	city: string
	currentPage: number
}

const EMPTY_GEO_DATA: any[] = [];

export const ListOnOfficeHub = ({
	isMobile,
	initialGeoData,
	initialLocation,
	initialState,
	initialCity,
	initialUserCurrency,
	parsedQuery,
	isSticky = false,
	regionData,
	headingAs,
	staticTitle
}) => {

	const navigate = useNavigate()

	// Search filters state
	const [filters, setFilters] = useState<SearchFilters>({
		desks: 3,
		office: "superior" as const,
		spaceType: "desks",
		area: 50,
		units: "sm" as const,
		location: initialLocation,
		state: initialState,
		city: initialCity,
		currentPage: 1,
	})

	// API data state
	const [geoData, setGeoData] = useState(initialGeoData)
	const [userCurrency, setUserCurrency] = useState(initialUserCurrency)
	const [isLoading, setIsLoading] = useState(false)
	const [recalibrate, setRecalibrate] = useState(false);
	// Memoized calculations
	const totalPages = useMemo(
		() => Math.ceil((geoData?.length || 0) / PAGINATION_ITEM_PER_PAGE) || 1,
		[geoData?.length],
	)

	const listingDataSource = useMemo(() => {
		const data = geoData

		if (data && data?.length > 0) {
			return data
		}

		return []
	}, [geoData])

	const baseRate = useMemo(
		() =>
			getAverageCostPerWorkstation(
				listingDataSource,
				filters.office,
				filters.spaceType,
				filters.area,
				filters.units as "sm" | "sf",
			),
		[listingDataSource, filters.office, filters.spaceType, filters.area, filters.units],
	)

	const calculation = useMemo(() => {
		if (!filters.location || !filters.office || baseRate === 0) return 0
		const multiplier = filters.spaceType === "desks" ? filters.desks : filters.area
		return Math.ceil(baseRate * multiplier) || 0
	}, [
		baseRate,
		filters.location,
		filters.office,
		filters.spaceType,
		filters.desks,
		filters.area,
	])


	// Geo search function
	const callGeoSearch = useCallback(
		async (searchOptions: Record<string, any> = {}) => {
			if (searchOptions.reset) {
				navigate("?")
				return
			}

			try {
				const newPage = searchOptions.page || filters.currentPage
				const response = await PropertiesService.getNearByProperties(searchOptions?.latitude, searchOptions?.longitude, 20, "nearby", 15, true);
				const responseData =
					response?.[0] || {};
				const newCurrency =
					responseData?.currencyIsoCode || initialUserCurrency;

				// Fallback for country/state/city if not set in searchOptions
				const resolvedCountry = searchOptions?.country || responseData?.country || filters.location;
				const resolvedState = searchOptions?.state || responseData?.state || filters.state;
				const resolvedCity = searchOptions?.city || responseData?.city || filters.city;

				setUserCurrency(newCurrency)
				setGeoData(response)

				// Update filters with *resolved* location data
				setFilters(prev => ({
					...prev,
					location: resolvedCountry,
					state: resolvedState,
					city: resolvedCity,
					currentPage: newPage
				}));
			} catch (error) {
				console.error("Geo search error:", error)
			} finally {
				setIsLoading(false)
			}
		},
		[filters, navigate, initialUserCurrency],
	)

	// Event handlers
	const handleLocationSelect = async (location: string, displayText?: string) => {
		setIsLoading(true)
		const parsedLocation = parseSearchUrl(location) as Record<string, any>
		let { latitude, longitude } = parsedLocation

		// AutoPlaces area/city picks are coord-less path URLs (e.g.
		// /au/nsw/sydney), but the nearby search + map need a point. Resolve the
		// picked label to lat/lng via the places proxy (server-side key) when the
		// URL carries none — without this the map never recentres on search.
		if ((latitude == null || longitude == null) && displayText) {
			try {
				const res = await fetch(
					`/api/proxy/places/resolve?text=${encodeURIComponent(displayText)}&country=${encodeURIComponent(parsedLocation.country || "")}`,
				)
				if (res.ok) {
					const resolved = await res.json()
					if (resolved?.lat != null && resolved?.lng != null) {
						latitude = resolved.lat
						longitude = resolved.lng
					}
				}
			} catch (error) {
				console.error("Add-listing location resolve failed", error)
			}
		}

		await callGeoSearch({
			...parsedLocation,
			latitude,
			longitude,
			page: 1 // Reset to first page on location change
		})
		setRecalibrate(true)
	}



	const handlePageChange = useCallback(
		(newPage: number) => {
			if (newPage < 1 || newPage > totalPages || newPage === filters.currentPage) return
			callGeoSearch({ page: newPage })
		},
		[callGeoSearch, totalPages, filters.currentPage],
	)

	// Individual filter setters
	const setDesks = useCallback((desks: number) => setFilters(prev => ({ ...prev, desks })), [])
	const setOffice = useCallback((office: "premium" | "superior" | "economical") => setFilters(prev => ({ ...prev, office })), [])
	const setSpaceType = useCallback((spaceType: string) => setFilters(prev => ({ ...prev, spaceType })), [])
	const setArea = useCallback((area: number) => setFilters(prev => ({ ...prev, area })), [])
	const setUnits = useCallback((units: "sm" | "sf") => setFilters(prev => ({ ...prev, units })), [])
	const setCity = useCallback((city: string) => setFilters(prev => ({ ...prev, city })), [])

	// Sync with loader data on route changes
	useEffect(() => {
		setFilters(prev => ({
			...prev,
			location: initialLocation,
			state: initialState,
			city: initialCity,
			currentPage: 1,
		}))
	}, [initialLocation, initialState, initialCity])

	return (
		<div className='relative  bg-white'>
			<div className='relative mx-auto'>
				<div className='grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 items-center'>
					<div className='w-full md:rounded-lg overflow-hidden h-[250px] md:h-[500px] order-1 lg:order-2'>
						<Suspense
							fallback={
								<div className='h-full w-full flex items-center justify-center'>
									{isLoading && "Loading map..."
									}                                </div>
							}
						>
							<ClientOnly fallback={<div id='skeleton' />}>
								{() => (
									<CustomMap
										data={geoData?.length ? geoData : EMPTY_GEO_DATA}
										totalListings={geoData?.length}
										currentPage={filters.currentPage}
										handlePageChange={handlePageChange}
										callGeoSearch={callGeoSearch}
										mobile={isMobile}
										loading={isLoading}
										showNext={false}
										showPerDesk={true}
										handleLocationSelect={handleLocationSelect}
										isPropertyCard={true}
										recalibrate={recalibrate}
										setRecalibrate={setRecalibrate}
										parsedQuery={parsedQuery}
									/>
								)}
							</ClientOnly>
						</Suspense>
					</div>
					<div className='flex justify-center px-4 md:px-0 order-2 lg:order-1'>
						<AddListingCalculator
							calculation={calculation}
							desks={filters.desks}
							setDesks={setDesks}
							office={filters.office}
							setOffice={setOffice}
							baseRate={baseRate}
							spaceType={filters.spaceType}
							setSpaceType={setSpaceType}
							area={filters.area}
							setArea={setArea}
							units={filters.units as "sm" | "sf"}
							setUnits={setUnits}
							userCurrency={userCurrency}
							handleLocationSelect={handleLocationSelect}
							listingDataSource={listingDataSource}
							isMobile={isMobile}
							isSticky={isSticky}
						regionData={regionData}
						headingAs={headingAs}
						staticTitle={staticTitle}
					/>
					</div>
				</div>
			</div>
		</div>
	);
};
